import { useTranslation } from 'react-i18next';

import {
  comparisonGroupKey,
  comparisonRowKey,
  formatComparisonValue,
  isNumericUnit,
  type ComparisonState,
} from '@entities/selection';
import { ComparisonTable, type ComparisonRow } from '@entities/vehicle/ComparisonTable';

import styles from './SelectionComparison.module.css';

interface SelectionComparisonProps {
  comparison: ComparisonState;
  currency: string;
}

/**
 * «Schneller Vergleich» — таблица сравнения четвёрки (FE-220, MVP_PRD §34.4).
 *
 * Три правила, которые здесь не декоративны:
 *
 * * **группы не смешиваются и не сводятся в общий балл** (§34.5). «Какой
 *   автомобиль», «как отвечал автохаус» и «что известно об автохаусе» — три
 *   разных вопроса, и каждая группа получает собственный заголовок;
 * * **лучшее значение приходит с сервера**, а не вычисляется здесь: направление
 *   («меньше — лучше» у пробега и цены) — часть контракта, а не догадка фронта;
 * * **код без перевода не показывается.** Строка, подпись которой отсутствует в
 *   локали, пропускается: `market_delta_cents` на экране — утечка внутренностей.
 */
export function SelectionComparison({ comparison, currency }: SelectionComparisonProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? 'de';

  const columns = comparison.columns.map((column) =>
    [column.make, column.model].filter(Boolean).join(' '),
  );

  const context = {
    locale,
    currency,
    translateCode: (code: string) => {
      // Значение справочника переводится по тем же префиксам, что и всюду
      // (SKILLS S-13): собственного словаря у таблицы нет.
      for (const prefix of ['catalog.fuel', 'catalog.transmission', 'catalog.drivetrain']) {
        const key = `${prefix}.${code}`;
        if (i18n.exists(key)) return t(key);
      }
      return null;
    },
    translateUnit: (key: string, value: string) => t(key, { value }),
  };

  const groups = comparison.groups
    .map((group) => ({
      code: group.code,
      rows: group.rows.flatMap<ComparisonRow>((row) => {
        const labelKey = comparisonRowKey(row.code);
        if (!i18n.exists(labelKey)) return [];
        return [
          {
            labelKey,
            cells: row.values.map((value) => ({
              text: formatComparisonValue(value, row.unit, context),
              numeric: isNumericUnit(row.unit),
            })),
            bestIndexes: row.best_indexes,
          },
        ];
      }),
    }))
    .filter((group) => group.rows.length > 0);

  if (columns.length === 0 || groups.length === 0) return null;

  return (
    <section className={styles.section} aria-labelledby="comparison-title">
      <div className={styles.head}>
        <h2 id="comparison-title" className={styles.title}>
          {t('selection.compare.title')}
        </h2>
        <p className={styles.note}>{t('selection.compare.note')}</p>
      </div>

      {groups.map((group) => (
        <div key={group.code} className={styles.group}>
          <h3 className={styles.groupTitle}>{t(comparisonGroupKey(group.code))}</h3>
          <ComparisonTable
            columns={columns}
            rows={group.rows}
            caption={t(comparisonGroupKey(group.code))}
            containerClassName={styles.table}
          />
        </div>
      ))}
    </section>
  );
}
